<template>
  <div class="Carousel">

    <div class="Carousel-slider"
      ref="slider"
      :style="{ transform: `translate3d(${moveX}px, 0, 0)` }">

      <slot></slot>

    </div>

  </div>
</template>

<script>
export default {
  name: 'Carousel',

  props: {
    initialIndex: {
      type: Number,
      default: 0
    },
    next: {
      type: Boolean,
      default: false
    },
    prev: {
      type: Boolean,
      default: false
    },
    restart: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      moveX: 0,
      midpoint: 0,
      sliderX: 0,
      targets: [],
      isDragging: false,
      isSettled: false,
      dragStartX: 0,
      sliderDragStartX: 0,
      velocity: 0,
      isAnimating: false,
      restingFrames: 0,
      accel: 0,
      selectedIndex: 0,
      friction: 0.3,
      previousX: 0,
      currentTime: Date.now(),
      previousTime: Date.now()
    }
  },

  mounted () {
    this.$nextTick(this.init)
  },

  beforeDestroy () {
    this.destroy()
  },

  watch: {
    sliderX () {
      let x = (this.sliderX * -1) - this.midpoint
      this.dispatchEvent('slide', { x: x * -1 })
    },

    selectedIndex () {
      this.dispatchEvent('selected', { index: this.selectedIndex })
    },

    initialIndex () {
      this.slideTo(this.initialIndex)
    },

    next () {
      if (this.next) this.goNext()
    },

    prev () {
      if (this.prev) this.goPrevious()
    },

    restart () {
      if (this.restart) this.goRestart()
    }
  },

  methods: {
    /**
     * Initialization
     */
    init () {
      this.initSlider()

      // Only add listeners if there is more than 1 item
      if (this.$items.length > 1) return this.addEventListeners()
      this.removeEventListeners()
    },

    /**
     * Destroy
     */
    destroy () {
      this.removeEventListeners()
    },

    /**
     * Initialize slider
     */
    initSlider () {
      let totalWidth = 0

      // Calculate element midpoint
      this.midpoint = this.$el.offsetWidth / 2

      // Initialize sliderX
      this.sliderX = -this.midpoint

      // Initialize targets
      this.targets = []

      // Select items
      this.$items = [].slice.call(this.$refs.slider.children)

      // Loop through items
      this.$items.forEach(($item, index) => {
        // Position items
        $item.style.left = `${totalWidth}px`
        totalWidth += $item.offsetWidth

        // Calculate item target (midpoint)
        this.targets.push($item.offsetLeft + ($item.offsetWidth / 2))
      })

      this.dispatchEvent('initialized')

      // Move to initial slide
      this.moveTo(this.initialIndex)
    },

    /**
     * Move to a slide by index
     * @param {Integer} index
     */
    moveTo (index) {
      const target = this.targets[index]

      this.selectedIndex = index
      this.sliderX = -target

      this.move(-target + this.midpoint)
      this.dispatchEvent('moveTo', { index })
    },

    /**
     * Slide to a slide by index
     * @param  {Integer} index
     */
    slideTo (index) {
      this.selectedIndex = index
      this.startAnimation()
    },

    /**
     * Go to next item
     */
    goNext () {
      const index = this.selectedIndex < this.$items.length - 1
        ? this.selectedIndex + 1
        : 0

      this.slideTo(index)
    },

    /**
     * Go to previous item
     */
    goPrevious () {
      const index = this.selectedIndex > 0
        ? this.selectedIndex - 1
        : this.$items.length - 1

      this.slideTo(index)
    },

    goRestart () {
      this.slideTo(this.initialIndex)
    },

    /**
     * Dispatch an event
     * @param {String} name
     * @param {Object} payload
     */
    dispatchEvent (name, payload) {
      const event = new CustomEvent(name, { detail: payload })
      this.$el.dispatchEvent(event)
    },

    /**
     * Add event listeners
     */
    addEventListeners () {
      this.$el.addEventListener('touchstart', this.handlePointerStart)
      this.$el.addEventListener('touchmove', this.handlePointerMove)
      this.$el.addEventListener('touchend', this.handlePointerEnd)
      this.$el.addEventListener('mousedown', this.handlePointerStart)
      this.$el.addEventListener('mousemove', this.handlePointerMove)
      this.$el.addEventListener('mouseup', this.handlePointerEnd)
    },

    /**
     * Remove event listeners
     */
    removeEventListeners () {
      this.$el.removeEventListener('touchstart', this.handlePointerStart)
      this.$el.removeEventListener('touchmove', this.handlePointerMove)
      this.$el.removeEventListener('touchend', this.handlePointerEnd)
      this.$el.removeEventListener('mousedown', this.handlePointerStart)
      this.$el.removeEventListener('mousemove', this.handlePointerMove)
      this.$el.removeEventListener('mouseup', this.handlePointerEnd)
    },

    /**
     * Handle pointer start event
     */
    handlePointerStart (event) {
      // event.preventDefault() // preventing content clicks
      this.isDragging = true
      this.isSettled = false
      this.dragStartX = this.xPositionFromPointerEvent(event)
      this.sliderDragStartX = this.sliderX
      this.velocity = 0
      this.startAnimation()
      this.dispatchEvent('pointerStart')
    },

    /**
     * Handle pointer move event
     */
    handlePointerMove (event) {
      if (!this.isDragging) return
      let moveX

      // Mark current slider x position and time
      this.previousX = this.sliderX
      this.previousTime = this.currentTime

      moveX = this.xPositionFromPointerEvent(event) - this.dragStartX
      this.sliderX = this.sliderDragStartX + moveX
      this.currentTime = Date.now()
    },

    /**
     * Handle pointer end event
     */
    handlePointerEnd (event) {
      const prevIndex = this.selectedIndex
      const itemCount = this.$items.length
      let minDistance = Infinity
      let distance

      this.dispatchEvent('pointerEnd')

      if (this.previousX) {
        this.velocity = (this.sliderX - this.previousX) / (this.currentTime - this.previousTime)
        this.velocity *= 1000 / 60
        this.estimateX = this.getRestingPosition()
        this.previousX = null
      } else {
        this.estimateX = this.sliderX
      }

      // Get closest attractor to end position
      for (let i = 0; i < itemCount; i++) {
        const target = this.targets[i]
        distance = Math.abs(-this.estimateX - target)

        if (distance < minDistance) {
          this.selectedIndex = i
          minDistance = distance
        }
      }

      // If not enough velocity to escape current attractor, boost it
      if (this.selectedIndex === prevIndex) {
        let selectedTarget = this.targets[this.selectedIndex]
        distance = -this.sliderX - selectedTarget

        if (distance > 0 && this.velocity > 1 && this.$items[this.selectedIndex - 1]) {
          this.selectedIndex--
        }
      }

      this.isDragging = false
    },

    /**
     * Get x position from pointer event (touch or mouse)
     */
    xPositionFromPointerEvent (event) {
      return event.touches ? event.touches[0].pageX : event.pageX
    },

    /**
     * Start animation loop
     */
    startAnimation () {
      if (this.isAnimating) return
      this.isAnimating = true
      this.restingFrames = 0
      this.animate()
    },

    /**
     * The animation loop
     */
    animate () {
      let moveX = this.sliderX + this.midpoint
      const prevX = this.sliderX

      if (!this.isDragging) {
        this.applyForce(this.getAttraction())
      }

      this.update()
      this.move(moveX)
      this.settle(prevX)

      if (this.isAnimating) {
        window.requestAnimationFrame(this.animate.bind(this))
      }
    },

    /**
     * Apply force
     * @param  {Number} force
     */
    applyForce (force) {
      this.accel += force
    },

    /**
     * Get current attraction
     */
    getAttraction () {
      const target = this.targets[this.selectedIndex]
      const distance = -target - this.sliderX
      const force = distance * 0.03
      return force
    },

    /**
     * Update slider physics
     */
    update () {
      this.velocity += this.accel
      this.velocity *= (1 - this.friction)
      this.sliderX += this.velocity
      this.accel = 0
    },

    /**
     * Move slider
     * @param {Number} x Horizontal position
     */
    move (x) {
      this.moveX = x
    },

    /**
     * Settle slider
     * @param {Number} prevX
     */
    settle (prevX) {
      if (!this.isDragging && Math.round(this.sliderX * 100) === Math.round(prevX * 100)) {
        this.restingFrames++
      }

      if (this.restingFrames > 2) {
        this.isAnimating = false
        this.isSettled = true
        this.$nextTick(() => {
          this.dispatchEvent('settled')
        })
      }
    },

    /**
     * Get slider resting position
     * @return {Number}
     */
    getRestingPosition () {
      const restingVelocity = 0.07
      let velocity = this.velocity
      let restX = this.sliderX

      while (Math.abs(velocity) > restingVelocity) {
        velocity *= 1 - this.friction
        restX += velocity
      }

      return restX
    }
  }
}
</script>

<style>
.Carousel {
  overflow-x: hidden;
}

.Carousel-slider {
  position: relative;
}

.Carousel-slider > * {
  position: absolute;
}
</style>



// WEBPACK FOOTER //
// Carousel.vue?4a644468